// Every screen picks up a new frontend deploy by itself (operating laws 4 and 5).
//
// A tab that is never reloaded runs its old bundle for ever: on 2026-10-22 a
// billing PC was still on main.518b0beb weeks after it was replaced. This asks
// the server which bundle it serves (asset-manifest.json, the same file
// CaptainPAD's AssetServer prefetches from) and reloads when it differs — but
// only when nobody is in the middle of something: the screen is in the
// background or untouched for `idleMs`, AND no field is focused, no dialog is
// open, and no screen has registered unsaved work (registerUpdateBlocker — the
// till's cart lives only in React state).
//
// The pad needs nothing extra: its AssetServer fetches index.html network-first,
// so the reload lands on the new build.

import { bundleVersion } from "@/lib/interactionLog/sync";
import { flushLogs, logEvent } from "@/lib/interactionLog";

const ATTEMPT_KEY = "vz-update-attempt";
// One reload per target bundle per window: if the reload comes back on the old
// bundle (a cache answered), we do not reload in a loop.
const RETRY_WINDOW_MS = 10 * 60_000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];
// Both dialog systems (Radix and the legacy <Modal>) render one of these.
const OPEN_DIALOG = '[role="dialog"], [role="alertdialog"]';
const FLUSH_WAIT_MS = 1_000;

const blockers = new Set();

/**
 * A screen holding work that exists nowhere but in memory calls this with a
 * function that answers "is there unsaved work right now?". Returns the release.
 */
export function registerUpdateBlocker(isBusy) {
  blockers.add(isBusy);
  return () => blockers.delete(isBusy);
}

function hasUnsavedWork() {
  if (document.querySelector(OPEN_DIALOG)) return true;
  for (const isBusy of blockers) {
    try {
      if (isBusy()) return true;
    } catch {
      return true;
    }
  }
  return false;
}

// window.name survives a reload in the same tab, so the loop guard still works
// where sessionStorage is refused.
const windowNameStorage = {
  getItem: () => (window.name.startsWith(ATTEMPT_KEY) ? window.name.slice(ATTEMPT_KEY.length + 1) : null),
  setItem: (_key, value) => { window.name = `${ATTEMPT_KEY}=${value}`; },
};

function servedVersion(manifest) {
  const main = manifest?.files?.["main.js"];
  const match = typeof main === "string" ? main.match(/main\.[0-9a-f]+/) : null;
  return match ? match[0] : null;
}

function isTyping() {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}

function safeStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function startVersionWatch({
  currentVersion = bundleVersion(),
  intervalMs = 60_000,
  idleMs = 20_000,
  tickMs = 5_000,
  now = () => Date.now(),
  fetchImpl = (...args) => window.fetch(...args),
  reload = () => window.location.reload(),
  storage = safeStorage(),
  flush = flushLogs,
} = {}) {
  if (!currentVersion || currentVersion === "dev") return { stop() {} };

  let latest = null;
  let lastActivity = now();
  let stopped = false;

  const readAttempt = () => {
    for (const store of [storage, windowNameStorage]) {
      try {
        const raw = store?.getItem(ATTEMPT_KEY);
        if (raw) return JSON.parse(raw);
      } catch {
        // Try the next store.
      }
    }
    return null;
  };

  const writeAttempt = value => {
    for (const store of [storage, windowNameStorage]) {
      try {
        store?.setItem(ATTEMPT_KEY, value);
        return true;
      } catch {
        // Try the next store.
      }
    }
    return false;
  };

  const recentlyAttempted = target => {
    const attempt = readAttempt();
    return Boolean(attempt) && attempt.to === target && now() - attempt.at < RETRY_WINDOW_MS;
  };

  const tryReload = async () => {
    if (stopped || !latest || latest === currentVersion) return;
    if (recentlyAttempted(latest)) return;
    if (isTyping() || hasUnsavedWork()) return;
    if (!document.hidden && now() - lastActivity < idleMs) return;
    // No way to remember the attempt means no way to stop a loop: stay put.
    if (!writeAttempt(JSON.stringify({ to: latest, at: now() }))) return;
    stopped = true;
    logEvent("app_update", { from: currentVersion, to: latest, hidden: Boolean(document.hidden) });
    try {
      await Promise.race([flush(), new Promise(resolve => setTimeout(resolve, FLUSH_WAIT_MS))]);
    } catch {
      // Logging must never hold back the update.
    }
    reload();
  };

  const check = async () => {
    if (stopped) return;
    try {
      // No query string: the service worker caches by URL, and a buster would
      // add an entry every minute. no-store already skips the HTTP cache.
      const response = await fetchImpl("/asset-manifest.json", { cache: "no-store" });
      if (!response?.ok) return;
      const served = servedVersion(await response.json());
      if (served) latest = served;
    } catch {
      // Server unreachable: keep running what we have. Not a reason to reload.
      return;
    }
    tryReload();
  };

  const onActivity = () => {
    lastActivity = now();
  };
  const onVisibility = () => {
    if (document.hidden) tryReload();
    else check();
  };

  ACTIVITY_EVENTS.forEach(name => document.addEventListener(name, onActivity, { capture: true, passive: true }));
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", check);
  const pollTimer = setInterval(check, intervalMs);
  const tickTimer = setInterval(tryReload, tickMs);
  check();

  return {
    stop() {
      stopped = true;
      clearInterval(pollTimer);
      clearInterval(tickTimer);
      ACTIVITY_EVENTS.forEach(name => document.removeEventListener(name, onActivity, { capture: true }));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", check);
    },
  };
}
